import { faFloppyDisk } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropdownMenu } from '@nexelem/components';
import { type SelectedSavedViewData, useSaveViewStore } from '../SaveViewStore';
import { type TableState, useTableName } from '../../utils';
import { useTableTranslation } from '../../translations';
import { openSaveViewModal } from '../SaveViewModal/SaveViewModal';
import { openOverrideCurrentViewModal } from './OverrideCurrentViewModal';
import { useIsDirtyView } from '../../filters/useIsDirtyView';
import { Decoder } from '../../filters/Decoder';
import { useFormContext } from 'react-hook-form';
import { useCallback } from 'react';

type OverrideCurrentViewButtonProps = {
  data: SelectedSavedViewData;
};

export const OverrideCurrentViewButton = ({ data }: OverrideCurrentViewButtonProps) => {
  const { t } = useTableTranslation();
  const tableName = useTableName();
  const saveView = useSaveViewStore((state) => state.saveView);
  const isDirtyView = useIsDirtyView();
  const { getValues } = useFormContext<TableState>();

  const encodeCurrentView = useCallback(async () => Decoder.encode(getValues()), [getValues]);

  const handleOverride = useCallback(async () => {
    const encoded = await encodeCurrentView();
    saveView(tableName, data.id, { name: data.name, description: data.description, data: encoded });
  }, [encodeCurrentView, saveView, tableName, data.id, data.name, data.description]);

  const handleSaveAsNewView = useCallback(async () => {
    const encoded = await encodeCurrentView();
    openSaveViewModal({ tableName, data: encoded });
  }, [encodeCurrentView, tableName]);

  return (
    <DropdownMenu.Button
      disabled={!isDirtyView}
      StartIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
      onClick={() =>
        openOverrideCurrentViewModal({
          savedViewName: data.name,
          onOverride: () => void handleOverride(),
          onSaveAsNewView: () => void handleSaveAsNewView(),
        })
      }
    >
      {t('overrideCurrentView')}
    </DropdownMenu.Button>
  );
};
